// src/layouts/trafficmonitoring/routeFlows/RouteFlowsFilter.js
import { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Stack } from "@mui/material";
import MDDatePicker from "components/MDDatePicker";
import MDButton from "components/MDButton";

function RouteFlowsFilter({
  selectedDate,
  onSelectedDateChange,
  onApply,
  onLast24h,
}) {
  const { t } = useTranslation();
  // Normalizer: Date | [Date] | ISO string | "dd/mm/yyyy" -> "YYYY-MM-DD"
  const toISODateOnly = (input) => {
    if (!input) return null;
    if (Array.isArray(input)) input = input[0] ?? null;

    if (input instanceof Date && !Number.isNaN(input.getTime())) {
      return new Date(Date.UTC(input.getFullYear(), input.getMonth(), input.getDate()))
        .toISOString()
        .slice(0, 10);
    }

    if (typeof input === "string") {
      if (/^\d{4}-\d{2}-\d{2}$/.test(input)) return input; // already YYYY-MM-DD

      if (/^\d{4}-\d{2}-\d{2}T/.test(input)) {
        const d = new Date(input);
        if (!Number.isNaN(d.getTime())) {
          return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()))
            .toISOString()
            .slice(0, 10);
        }
      }

      const m = input.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/); // dd/mm/yyyy
      if (m) {
        const dd = +m[1], mm = +m[2] - 1, yy = +m[3];
        const d = new Date(Date.UTC(yy, mm, dd));
        if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
      }

      const parsed = new Date(input);
      if (!Number.isNaN(parsed.getTime())) {
        return new Date(Date.UTC(parsed.getFullYear(), parsed.getMonth(), parsed.getDate()))
          .toISOString()
          .slice(0, 10);
      }
    }
    return null;
  };


const buildInTimestamp = (input) => {
  if (!input) return null;
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return null;
  
  return d.toISOString().replace(/\.\d{3}Z$/, "Z");
  
};

  const controls = useMemo(
    () => (
      <Stack direction="row" spacing={2} alignItems="center">
<MDDatePicker
  input={{
    placeholder: "dd/mm/yyyy HH:MM",
    sx: {
      color: "#344767",
      "&::placeholder": { color: "#eef1f6", opacity: 1 },
      "& input::placeholder": { color: "#eef1f6", opacity: 1 },
      "& .MuiInputBase-input": { color: "#eef1f6" },
    },
  }}
  options={{
    enableTime: true,        
    time_24hr: true,         
    dateFormat: "d/m/Y H:i", 
    altFormat: "d/m/Y H:i",
    maxDate: new Date(),
  }}
  value={selectedDate}
  onChange={(selectedDates) => {
    onSelectedDateChange?.(selectedDates?.[0] ?? null);
  }}
/>

        <MDButton
          variant="gradient"
          color="info"
          disabled={!selectedDate}
          onClick={() => {
            const inTs = buildInTimestamp(selectedDate);            // "YYYY-MM-DDT14:00:00Z"            
            onApply(inTs); 
          }}
        >
          {t("common.buttons.apply").toUpperCase()}
        </MDButton>

        <MDButton
          variant="gradient"
          color="light"
          onClick={() => {
            onSelectedDateChange?.(null);
            onLast24h?.();
          }}
        >
          {t("trafficMonitoring.routeFlows.last1h")}
        </MDButton>
      </Stack>
    ),
    [selectedDate, onSelectedDateChange, onApply, onLast24h, t]
  );

  return controls;
}

export default memo(RouteFlowsFilter);
